import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Col,
  Table,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { useAccount } from "../../context/AccountContext";
import PageTitleAlt2 from "../../Layout/AppMain/PageTitleAlt2";
import PayoffChart from "../../components/strategy/PayoffChart";
import DhssUniversePicker from "../../components/strategy/DhssUniversePicker";

const STRATEGY_TYPES = {
  PYRAMID_HEDGING: "PYRAMID_HEDGING",
  DELTA_HEDGED_SHORT_STRANGLE: "DELTA_HEDGED_SHORT_STRANGLE",
};

// Fields that are more single-stock tuning knobs than portfolio-level settings —
// mirrors StrategyBuild.jsx's ADVANCED_SETTINGS always-hide list so the shared
// panel here doesn't surface anything the per-symbol search already decides.
// max_call_lots is fixed internally (PORTFOLIO_MAX_CALL_LOTS in
// portfolio_allocator.py) — max_investment (still shown below) is the actual
// search variable now; see that module's docstring for why. PYRAMID_HEDGING only —
// DHSS's settings shape is different and doesn't need this filtering (see
// ALWAYS_HIDE_BY_TYPE below).
const PH_ALWAYS_HIDE = [
  "tail_risk_pct", "allow_tail_risk", "rollout_mode",
  "opening_call_delta", "opening_put_delta", "opening_lots",
  "max_call_lots",
  "average_buy_price_to_call_strike_ratio", "cash_saving_mode",
  "min_pct_hedge", "max_pct_hedge",
];

const ALWAYS_HIDE_BY_TYPE = {
  [STRATEGY_TYPES.PYRAMID_HEDGING]: PH_ALWAYS_HIDE,
  [STRATEGY_TYPES.DELTA_HEDGED_SHORT_STRANGLE]: [],
};

// Portfolio Builder is budget-driven, not hedge-band-driven: for a brand-new
// position hedge% is mathematically identical to the call's own delta no
// matter how many lots are bought (numerator and denominator both scale with
// call_lots), so a fixed Minimum Hedge % is often unsatisfiable — and as the
// ladder builds up over multiple lots, legitimate multi-lot hedges can show
// hedge% figures far outside a "sane-looking" 0-100 band (see
// strategies/pyramid_hedging.py hedge_pct calc). These overrides make the
// min/max hedge% check effectively a no-op here; Income-First Mode is forced
// on so the separate Hedge Style/ratio check (which IS bypassed by CSM) never
// blocks either. Single-stock StrategyBuild and the account Settings page are
// unaffected — this only changes what Portfolio Builder sends per request.
// PYRAMID_HEDGING only — DHSS has no equivalent hedge-band check to bypass.
const PH_FORCED_OVERRIDES = {
  cash_saving_mode: true,
  min_pct_hedge: 0,
  max_pct_hedge: 100000,
};

const FORCED_OVERRIDES_BY_TYPE = {
  [STRATEGY_TYPES.PYRAMID_HEDGING]: PH_FORCED_OVERRIDES,
  [STRATEGY_TYPES.DELTA_HEDGED_SHORT_STRANGLE]: {},
};

const POLL_INTERVAL_MS = 1500;

const fmtMoney = (n) => {
  if (n == null || isNaN(n)) return "—";
  return `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
};

const parseSymbols = (text) =>
  [...new Set(
    text
      .split(/[\n,]/)
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean)
  )];

const PortfolioBuilder = () => {
  const { selectedAccount } = useAccount();
  const username = selectedAccount;

  const [strategyType, setStrategyType] = useState(STRATEGY_TYPES.PYRAMID_HEDGING);
  const [symbolsText, setSymbolsText] = useState("");
  const [dhssSelected, setDhssSelected] = useState(new Set());
  const [dhssManualOpen, setDhssManualOpen] = useState(false);
  const [totalCapital, setTotalCapital] = useState("");
  const [reservePct, setReservePct] = useState(10);

  const [settingsSchema, setSettingsSchema] = useState(null);
  const [dynamicValues, setDynamicValues] = useState({});
  const [availableExpiries, setAvailableExpiries] = useState([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const [error, setError] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [progress, setProgress] = useState({ completed: 0, total: 0, current_symbol: null });
  const [results, setResults] = useState([]);
  const [selectedRows, setSelectedRows] = useState({});
  const [recomputingSymbol, setRecomputingSymbol] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  const [detailsModal, setDetailsModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);

  const pollTimer = useRef(null);

  // Switching strategy type mid-session would otherwise leave stale results/
  // selections from the other type on screen — clear them on toggle.
  useEffect(() => {
    setResults([]);
    setJobId(null);
    setJobStatus(null);
    setSelectedRows({});
    setError(null);
    setSaveMessage(null);
    setDhssSelected(new Set());
  }, [strategyType]);

  // ------------------------------------------------
  // FETCH SHARED SETTINGS (re-fetched whenever strategyType changes)
  // ------------------------------------------------
  useEffect(() => {
    if (!username) return;
    axios.get("/strategy/settings", { params: { strategy_type: strategyType, username } })
      .then((res) => {
        setSettingsSchema(res.data);
        const initial = {};
        Object.entries(res.data.variable_inputs || {}).forEach(([key, config]) => {
          initial[key] = config.default !== undefined ? config.default : "";
        });
        Object.entries(res.data.user_settings || {}).forEach(([key, config]) => {
          initial[key] = config.value !== undefined ? config.value : "";
        });
        Object.assign(initial, FORCED_OVERRIDES_BY_TYPE[strategyType] || {});
        setDynamicValues(initial);
      })
      .catch(() => setError("Failed to load strategy settings."));
  }, [username, strategyType]);

  useEffect(() => {
    axios.get("/expiries", { params: { strategy_type: strategyType } })
      .then((res) => {
        const exp = res.data.expiries || [];
        setAvailableExpiries(exp);
        if (exp.length > 0) {
          setDynamicValues((prev) => ({ ...prev, expiry: prev.expiry || exp[0] }));
        }
      })
      .catch(() => setAvailableExpiries([]));
  }, [strategyType]);

  // ------------------------------------------------
  // POLL JOB STATUS
  // ------------------------------------------------
  useEffect(() => {
    if (!jobId) return;

    const poll = async () => {
      try {
        const res = await axios.get(`/strategy/portfolio-plan/${jobId}`);
        setJobStatus(res.data.status);
        setProgress({
          completed: res.data.completed,
          total: res.data.total,
          current_symbol: res.data.current_symbol,
        });
        if (res.data.status === "COMPLETED" || res.data.status === "PARTIAL_FAILED") {
          const rows = res.data.results || [];
          setResults(rows);
          const sel = {};
          rows.forEach((r) => { sel[r.symbol] = r.status === "ok"; });
          setSelectedRows(sel);
        } else if (res.data.status === "FAILED") {
          setError(res.data.error || "Portfolio plan job failed.");
        } else {
          pollTimer.current = setTimeout(poll, POLL_INTERVAL_MS);
        }
      } catch (err) {
        setError("Failed to fetch job status.");
      }
    };

    poll();
    return () => clearTimeout(pollTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const buildSharedVariableInputs = () => ({ ...dynamicValues });

  const isDhss = strategyType === STRATEGY_TYPES.DELTA_HEDGED_SHORT_STRANGLE;

  const resolveSymbols = () =>
    isDhss
      ? [...dhssSelected, ...parseSymbols(symbolsText)].filter(
          (s, i, arr) => arr.indexOf(s) === i
        )
      : parseSymbols(symbolsText);

  const handleGeneratePlan = async () => {
    setError(null);
    setSaveMessage(null);
    setResults([]);
    setJobStatus(null);

    const symbols = resolveSymbols();
    if (symbols.length === 0) {
      setError(
        isDhss
          ? "Select at least one instrument from the universe below, or paste symbols manually."
          : "Enter at least one stock symbol."
      );
      return;
    }
    if (!totalCapital || Number(totalCapital) <= 0) {
      setError("Enter total capital to allocate.");
      return;
    }
    if (!dynamicValues.expiry) {
      setError("Select an option expiry before generating a plan.");
      return;
    }

    try {
      const res = await axios.post("/strategy/portfolio-plan", {
        username,
        strategy_type: strategyType,
        symbols,
        total_capital: Number(totalCapital),
        reserve_pct: Number(reservePct) || 0,
        variable_inputs: buildSharedVariableInputs(),
      });
      setJobId(res.data.job_id);
      setJobStatus(res.data.status);
      setProgress({ completed: 0, total: symbols.length, current_symbol: null });
    } catch (err) {
      setError(err.response?.data?.error || "Failed to start portfolio plan.");
    }
  };

  // ------------------------------------------------
  // ROW-LEVEL RECOMPUTE (edited budget) — reuses the same
  // job endpoint for a single symbol rather than a bespoke route.
  // ------------------------------------------------
  const recomputeRow = async (symbol, newBudget) => {
    if (!newBudget || Number(newBudget) <= 0) return;
    setRecomputingSymbol(symbol);
    try {
      const res = await axios.post("/strategy/portfolio-plan", {
        username,
        strategy_type: strategyType,
        symbols: [symbol],
        total_capital: Number(newBudget),
        reserve_pct: 0,
        variable_inputs: buildSharedVariableInputs(),
      });
      const rowJobId = res.data.job_id;

      // Short-lived poll scoped to this one symbol.
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        const statusRes = await axios.get(`/strategy/portfolio-plan/${rowJobId}`);
        if (["COMPLETED", "PARTIAL_FAILED", "FAILED"].includes(statusRes.data.status)) {
          const updated = (statusRes.data.results || [])[0];
          if (updated) {
            setResults((prev) => prev.map((r) => (r.symbol === symbol ? updated : r)));
          }
          break;
        }
      }
    } catch (err) {
      setError(`Failed to recompute ${symbol}.`);
    } finally {
      setRecomputingSymbol(null);
    }
  };

  const toggleRow = (symbol) => setSelectedRows((prev) => ({ ...prev, [symbol]: !prev[symbol] }));

  const handleSaveAll = async () => {
    setError(null);
    setSaveMessage(null);
    const strategiesToSave = results
      .filter((r) => selectedRows[r.symbol] && r.status === "ok" && r.build_result)
      .map((r) => ({ symbolname: r.symbol, strategy_state: r.build_result }));

    if (strategiesToSave.length === 0) {
      setError("No successful, selected strategies to save.");
      return;
    }

    setIsSaving(true);
    try {
      const res = await axios.post("/strategy/save", {
        username,
        strategy_type: strategyType,
        strategies: strategiesToSave,
      });
      setSaveMessage(
        `Saved ${res.data.saved_count} strategy(ies) as INACTIVE. ` +
        `Go to Manage Strategies to review and activate.`
      );
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save strategies.");
    } finally {
      setIsSaving(false);
    }
  };

  const totalBudget = results.reduce((sum, r) => sum + (r.budget || 0), 0);
  const totalProjected = results.reduce((sum, r) => sum + (r.projected_capital || 0), 0);
  const jobRunning = jobStatus === "CREATED" || jobStatus === "RUNNING";

  return (
    <>
      <PageTitleAlt2
        heading="Portfolio Builder"
        subheading={
          isDhss
            ? "Pick instruments from the DHSS screener and build 1-lot-per-symbol strangles in bulk"
            : "Allocate capital across many stocks at once and build Pyramid Hedging strategies in bulk"
        }
        icon="pe-7s-network icon-gradient bg-happy-itmeo"
      />

      <div className="mt-3 d-flex" style={{ gap: 8 }}>
        <Button
          size="sm"
          color={!isDhss ? "primary" : "secondary"}
          outline={isDhss}
          onClick={() => setStrategyType(STRATEGY_TYPES.PYRAMID_HEDGING)}
          disabled={jobRunning}
        >
          Pyramid Hedging
        </Button>
        <Button
          size="sm"
          color={isDhss ? "primary" : "secondary"}
          outline={!isDhss}
          onClick={() => setStrategyType(STRATEGY_TYPES.DELTA_HEDGED_SHORT_STRANGLE)}
          disabled={jobRunning}
        >
          Delta-Hedged Short Strangle
        </Button>
      </div>

      <Card className="mt-3">
        <CardHeader>{isDhss ? "Instruments & Capital" : "Symbols & Capital"}</CardHeader>
        <CardBody>
          {error && <Alert color="danger">{error}</Alert>}
          {saveMessage && <Alert color="success">{saveMessage}</Alert>}

          <Form>
            {isDhss ? (
              <div className="mb-3">
                <DhssUniversePicker
                  selected={dhssSelected}
                  onChange={setDhssSelected}
                  disabled={jobRunning}
                />
                <div className="mt-2">
                  <Button
                    size="sm" color="link" className="p-0"
                    onClick={() => setDhssManualOpen(!dhssManualOpen)}
                  >
                    {dhssManualOpen ? "▲ Hide" : "▼ Or paste symbols manually"}
                  </Button>
                  {dhssManualOpen && (
                    <Input
                      type="textarea"
                      rows={3}
                      className="mt-2"
                      placeholder={"AAPL\nMSFT\nNVDA\n..."}
                      value={symbolsText}
                      onChange={(e) => setSymbolsText(e.target.value)}
                      disabled={jobRunning}
                    />
                  )}
                </div>
                <small className="text-muted d-block mt-2">
                  {resolveSymbols().length} instrument(s) selected
                  {dhssSelected.size > 0 && parseSymbols(symbolsText).length > 0 &&
                    " (picker + manual, de-duplicated)"}
                </small>
              </div>
            ) : (
              <FormGroup>
                <Label>Symbols (one per line, or comma-separated)</Label>
                <Input
                  type="textarea"
                  rows={6}
                  placeholder={"AAPL\nMSFT\nNVDA\n..."}
                  value={symbolsText}
                  onChange={(e) => setSymbolsText(e.target.value)}
                  disabled={jobRunning}
                />
                <small className="text-muted">
                  {parseSymbols(symbolsText).length} symbol(s)
                </small>
              </FormGroup>
            )}

            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label>Total Capital ($)</Label>
                  <Input
                    type="number"
                    placeholder="e.g. 1000000"
                    value={totalCapital}
                    onChange={(e) => setTotalCapital(e.target.value)}
                    disabled={jobRunning}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Reserve %: {reservePct}%</Label>
                  <Input
                    type="range"
                    min={0}
                    max={50}
                    step={1}
                    value={reservePct}
                    onChange={(e) => setReservePct(e.target.value)}
                    disabled={jobRunning}
                  />
                  <small className="text-muted">Capital held back as a buffer, not allocated.</small>
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup>
                  <Label>Expiry</Label>
                  <Input
                    type="select"
                    value={dynamicValues.expiry || ""}
                    onChange={(e) => setDynamicValues({ ...dynamicValues, expiry: e.target.value })}
                    disabled={jobRunning}
                  >
                    <option value="">— Select expiry —</option>
                    {availableExpiries.map((exp) => (
                      <option key={exp} value={exp}>
                        {new Date(exp + "T00:00:00").toLocaleDateString("en-US", {
                          day: "2-digit", month: "short", year: "numeric",
                        })}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            {settingsSchema?.user_settings && (
              <Card className="mt-2" style={{ background: "#fafbfc" }}>
                <CardHeader
                  style={{ cursor: "pointer" }}
                  className="d-flex justify-content-between"
                  onClick={() => setAdvancedOpen(!advancedOpen)}
                >
                  <span>Shared Advanced Settings (applied to every symbol)</span>
                  <span>{advancedOpen ? "▲" : "▼"}</span>
                </CardHeader>
                {advancedOpen && (
                  <CardBody>
                    <Row>
                      {Object.entries(settingsSchema.user_settings)
                        .filter(([key]) => !(ALWAYS_HIDE_BY_TYPE[strategyType] || []).includes(key))
                        .map(([key, config]) => (
                          <Col md={4} key={key}>
                            <FormGroup>
                              <Label>{config.title}</Label>
                              {typeof config.value === "boolean" ? (
                                <Input
                                  type="select"
                                  value={String(dynamicValues[key] ?? false)}
                                  onChange={(e) =>
                                    setDynamicValues({ ...dynamicValues, [key]: e.target.value === "true" })
                                  }
                                  disabled={jobRunning}
                                >
                                  <option value="true">Yes</option>
                                  <option value="false">No</option>
                                </Input>
                              ) : typeof config.value === "string" ? (
                                <Input
                                  type="text"
                                  value={dynamicValues[key] || ""}
                                  onChange={(e) => setDynamicValues({ ...dynamicValues, [key]: e.target.value })}
                                  disabled={jobRunning}
                                />
                              ) : (
                                <Input
                                  type="number"
                                  value={dynamicValues[key] ?? ""}
                                  onChange={(e) => setDynamicValues({ ...dynamicValues, [key]: e.target.value })}
                                  disabled={jobRunning}
                                />
                              )}
                              <small className="text-muted">{config.detail}</small>
                            </FormGroup>
                          </Col>
                        ))}
                    </Row>
                    {isDhss ? (
                      <small className="text-muted">
                        Each symbol is built at Initial Lots Per Side (above, default 1) —
                        Portfolio Builder doesn't search for more lots the way Pyramid Hedging
                        does; it checks that budget covers that fixed lot count and reports
                        "budget too small" per symbol if not. Add more instruments instead of
                        raising lots to diversify further.
                      </small>
                    ) : (
                      <small className="text-muted">
                        Maximum Call Lots is not shown here — the allocator sizes each structure by
                        searching Maximum Capital Per Rung (above) until it uses as much of the
                        symbol's budget as the ladder math allows, so Maximum Call Lots is fixed
                        generously high internally instead of being a value you set per request.
                      </small>
                    )}
                  </CardBody>
                )}
              </Card>
            )}

            <div className="mt-3">
              <Button color="primary" onClick={handleGeneratePlan} disabled={jobRunning}>
                {jobRunning ? <Spinner size="sm" /> : "Generate Plan"}
              </Button>
            </div>
          </Form>

          {jobRunning && (
            <div className="mt-3">
              <div className="d-flex justify-content-between" style={{ fontSize: 13, color: "#666" }}>
                <span>
                  {jobStatus === "CREATED" ? "Starting…" : `Building ${progress.current_symbol || "…"}`}
                </span>
                <span>{progress.completed} / {progress.total}</span>
              </div>
              <div style={{ background: "#eee", borderRadius: 4, height: 8, marginTop: 4 }}>
                <div
                  style={{
                    width: `${progress.total ? (progress.completed / progress.total) * 100 : 0}%`,
                    background: "#545cd8", height: 8, borderRadius: 4,
                    transition: "width 0.3s",
                  }}
                />
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {results.length > 0 && (
        <Card className="mt-4">
          <CardHeader className="d-flex justify-content-between align-items-center">
            <span>Portfolio Plan</span>
            <span style={{ fontSize: 12, color: "#666" }}>
              Budget total: {fmtMoney(totalBudget)} · Projected capital: {fmtMoney(totalProjected)}
            </span>
          </CardHeader>
          <CardBody>
            <div style={{ overflowX: "auto" }}>
              <Table bordered hover responsive style={{ textAlign: "center", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>
                      <Input
                        type="checkbox"
                        checked={
                          results.some((r) => r.status === "ok") &&
                          results.filter((r) => r.status === "ok").every((r) => selectedRows[r.symbol])
                        }
                        onChange={(e) => {
                          const checked = e.target.checked;
                          const next = {};
                          results.forEach((r) => { next[r.symbol] = checked && r.status === "ok"; });
                          setSelectedRows(next);
                        }}
                      />
                    </th>
                    <th>Symbol</th>
                    <th>Budget ($)</th>
                    <th>{isDhss ? "Lots / Side" : "Achieved Lots"}</th>
                    <th>{isDhss ? "Est. Margin" : "Projected Capital"}</th>
                    <th>{isDhss ? "Call / Put" : "Call / CSP"}</th>
                    <th>Status</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <RowEditor
                      key={r.symbol}
                      row={r}
                      selected={!!selectedRows[r.symbol]}
                      onToggle={() => toggleRow(r.symbol)}
                      onRecompute={recomputeRow}
                      recomputing={recomputingSymbol === r.symbol}
                      onShowDetails={() => {
                        setSelectedResult(r.build_result);
                        setDetailsModal(true);
                      }}
                    />
                  ))}
                </tbody>
              </Table>
            </div>

            <div className="mt-3">
              <Button color="success" onClick={handleSaveAll} disabled={isSaving}>
                {isSaving ? <Spinner size="sm" /> : "Save All Selected"}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      <StrategyDetailsModal
        isOpen={detailsModal}
        toggle={() => setDetailsModal(false)}
        selectedResult={selectedResult}
        strategyType={strategyType}
      />
    </>
  );
};

// Small isolated component so each row's budget-edit box has its own local
// state and only triggers a recompute on blur/Enter, not on every keystroke.
const RowEditor = ({ row, selected, onToggle, onRecompute, recomputing, onShowDetails }) => {
  const [budgetInput, setBudgetInput] = useState(row.budget ?? "");

  useEffect(() => setBudgetInput(row.budget ?? ""), [row.budget]);

  const commit = () => {
    if (Number(budgetInput) !== row.budget) {
      onRecompute(row.symbol, budgetInput);
    }
  };

  return (
    <tr>
      <td>
        <Input type="checkbox" checked={selected} onChange={onToggle} disabled={row.status !== "ok"} />
      </td>
      <td style={{ fontWeight: 500 }}>{row.symbol}</td>
      <td>
        <Input
          type="number"
          bsSize="sm"
          value={budgetInput}
          onChange={(e) => setBudgetInput(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => e.key === "Enter" && commit()}
          disabled={recomputing}
          style={{ width: 110, display: "inline-block" }}
        />
      </td>
      <td>{recomputing ? <Spinner size="sm" /> : (row.achieved_lots ?? "—")}</td>
      <td>{recomputing ? "—" : fmtMoney(row.projected_capital)}</td>
      <td style={{ fontSize: 11 }}>
        {row.call_symbol && <div style={{ color: "#28a745" }}>{row.call_symbol}</div>}
        {row.csp_symbol && <div style={{ color: "#dc3545" }}>{row.csp_symbol}</div>}
      </td>
      <td>
        {row.status === "ok" ? (
          <span style={{ color: "#28a745" }}>✓ OK</span>
        ) : row.status === "budget_too_small" ? (
          <span title={row.warning} style={{ color: "#e67e22", cursor: "help" }}>⚠ Budget too small</span>
        ) : (
          <span
            title={[row.error, ...(row.suggestions || [])].filter(Boolean).join("\n")}
            style={{ color: "#dc3545", cursor: "help" }}
          >
            ✗ {row.error || "Failed"}
          </span>
        )}
      </td>
      <td>
        {row.build_result && (
          <Button size="sm" color="info" onClick={onShowDetails}>
            Details
          </Button>
        )}
      </td>
    </tr>
  );
};

// ── Strategy Details modal ──────────────────────────────────────────────
// Same PYRAMID_HEDGING structure/ladder/payoff view as StrategyBuild.jsx's
// Details modal, trimmed to just the PH branch since Portfolio Builder only
// ever builds PYRAMID_HEDGING strategies.
const fmtPnl = (v) => {
  if (v == null) return "—";
  const sign = v >= 0 ? "+" : "-";
  return `${sign}$${Math.abs(Math.round(v)).toLocaleString("en-US")}`;
};

const LadderTable = ({ structure }) => {
  const callQty    = (structure.target_lots || 1) * (structure.lot_size || 1);
  const callLtp    = structure.call_ltp    || 0;
  const callStrike = structure.call_strike || 0;
  const ltp        = structure.underlying_ltp || structure.reference_avg_price || 0;

  const hasCsp     = structure.csp_enabled && !structure.csp_error;
  const putLtp     = hasCsp ? (structure.put_ltp    || 0) : 0;
  const putQty     = hasCsp ? (structure.lot_size   || 1) : 0;

  const optionsPnlBase = callLtp * callQty + putLtp * putQty;

  return (
    <>
      <Table bordered style={{ marginTop: 16, fontSize: 13 }}>
        <thead>
          <tr>
            <th>Step</th>
            <th>Buy Price</th>
            <th style={{ color: "#888" }}>% from LTP</th>
            <th>Qty</th>
            <th>Investment</th>
            <th>Total Qty</th>
            <th>Avg Price</th>
            <th style={{ color: "#545cd8", whiteSpace: "nowrap" }}>Est. Hedge %†</th>
            <th style={{ color: "#6f42c1" }}>Position P&L‡</th>
            <th style={{ color: "#17a2b8" }}>Options P&L‡</th>
          </tr>
        </thead>
        <tbody>
          {structure.ladder.map(row => {
            const pctFromLtp    = ltp > 0 ? ((row.buy_price - ltp) / ltp * 100) : null;
            const stockPnl      = Math.round((row.buy_price - row.avg_price) * row.total_qty);
            const callIntrinsic = callStrike > 0 ? Math.max(0, row.buy_price - callStrike) * callQty : 0;
            const optionsPnl    = Math.round(optionsPnlBase - callIntrinsic);
            const hedgePct      = row.projected_hedge_pct != null ? row.projected_hedge_pct : null;
            return (
              <tr key={row.step}>
                <td>{row.step}</td>
                <td>${row.buy_price}</td>
                <td style={{ color: pctFromLtp >= 0 ? "#28a745" : "#dc3545", fontSize: 12 }}>
                  {pctFromLtp != null ? `${pctFromLtp >= 0 ? "+" : ""}${pctFromLtp.toFixed(1)}%` : "—"}
                </td>
                <td>{row.qty}</td>
                <td style={{ color: "#555" }}>${row.investment != null ? Math.round(row.investment).toLocaleString("en-US") : "—"}</td>
                <td>{row.total_qty}</td>
                <td>${row.avg_price}</td>
                <td style={{ color: "#545cd8", fontWeight: 600 }}>
                  {hedgePct != null ? `~${hedgePct}%` : "—"}
                </td>
                <td style={{ color: stockPnl >= 0 ? "#28a745" : "#dc3545", fontWeight: 600 }}>
                  {fmtPnl(stockPnl)}
                </td>
                <td style={{ color: optionsPnl >= 0 ? "#28a745" : "#dc3545" }}>
                  {fmtPnl(optionsPnl)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <div style={{ fontSize: 11, color: "#888", marginTop: 4, fontStyle: "italic" }}>
        † Est. Hedge % is a forward projection of delta-weighted hedge when the stock reaches each ladder step. For illustration purposes only. Actual hedge may vary with time to expiry and implied volatility.<br/>
        ‡ Illustrative mark-to-market <strong>if the stock were sitting at that rung's own price right now</strong> — not the strategy's projected outcome. Stock P&L = unrealized gain on accumulated shares at that price. Options P&L = premium collected minus the short call's intrinsic loss once price exceeds the call strike (ITM). For the actual profit floor this ladder is built to guarantee at full completion, see "Tail P&L at Completion" above.
      </div>
    </>
  );
};

// DHSS branch — call/put strikes, deltas, premiums, breakevens, max profit.
// None of the ladder/opening-strangle/covered-call PH concepts apply — DHSS
// builds are a fixed-lot strangle, not a stock accumulation ladder.
const DhssStructureDetails = ({ selectedResult, structure }) => (
  <>
    <div style={{ border: "1px solid #e0e0e0", borderRadius: "8px", padding: "16px", marginBottom: "20px", background: "#fafafa" }}>
      <h5 style={{ marginBottom: "12px" }}>Strangle Structure</h5>

      <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>
        Call (Short)
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", rowGap: "10px", marginBottom: 16 }}>
        <div><strong>Call Symbol</strong><br/>{structure.call_symbol ?? "—"}</div>
        <div><strong>Strike</strong><br/>{structure.call_strike != null ? `$${structure.call_strike}` : "—"}</div>
        <div><strong>Call LTP</strong><br/>{structure.call_ltp != null ? `$${structure.call_ltp}` : "—"}</div>
        <div><strong>Call Delta</strong><br/>{structure.call_abs_delta != null ? structure.call_abs_delta.toFixed(3) : "—"}</div>
        <div><strong>Breakeven</strong><br/>{structure.call_breakeven != null ? `$${structure.call_breakeven}` : "—"}</div>
      </div>

      <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginBottom: 6, fontWeight: 600, borderTop: "1px solid #eee", paddingTop: 12 }}>
        Put (Short)
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", rowGap: "10px", marginBottom: 16 }}>
        <div><strong>Put Symbol</strong><br/>{structure.put_symbol ?? "—"}</div>
        <div><strong>Strike</strong><br/>{structure.put_strike != null ? `$${structure.put_strike}` : "—"}</div>
        <div><strong>Put LTP</strong><br/>{structure.put_ltp != null ? `$${structure.put_ltp}` : "—"}</div>
        <div><strong>Put Delta</strong><br/>{structure.put_abs_delta != null ? structure.put_abs_delta.toFixed(3) : "—"}</div>
        <div><strong>Breakeven</strong><br/>{structure.put_breakeven != null ? `$${structure.put_breakeven}` : "—"}</div>
      </div>

      <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginBottom: 6, fontWeight: 600, borderTop: "1px solid #eee", paddingTop: 12 }}>Position</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", rowGap: "10px" }}>
        <div><strong>Underlying LTP</strong><br/>{structure.underlying_ltp != null ? `$${structure.underlying_ltp}` : "—"}</div>
        <div><strong>Lot Size</strong><br/>{structure.lot_size ?? "—"}</div>
        <div><strong>Lots / Side</strong><br/>{structure.lots_per_side ?? "—"}</div>
        <div><strong>Days to Expiry</strong><br/>{structure.days_to_expiry ?? "—"}</div>
        <div><strong>Net Delta</strong><br/>{structure.net_delta != null ? structure.net_delta.toFixed(4) : "—"}</div>
      </div>

      {structure.max_profit != null && (
        <div style={{
          marginTop: 14, padding: "10px 12px", borderRadius: 6,
          border: "1px solid #c3e6cb", background: "#d4edda",
        }}>
          <div style={{ fontSize: 11, color: "#555", textTransform: "uppercase", fontWeight: 600 }}>
            Max Profit (both legs expire worthless)
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#155724" }}>
            ${Math.round(structure.max_profit).toLocaleString("en-US")}
          </div>
          <div style={{ fontSize: 11, color: "#777", marginTop: 2 }}>
            Total premium collected ({structure.total_premium_per_share != null ? `$${structure.total_premium_per_share}/share` : "—"}) —
            not a guarantee; the position is managed (rolled/adjusted) if delta drifts or a leg is tested, per this account's DHSS rules.
          </div>
        </div>
      )}
    </div>

    <PayoffChart
      strategyType="DELTA_HEDGED_SHORT_STRANGLE"
      derived={structure}
      reference={selectedResult?.reference}
      intent={selectedResult?.intent}
      title="Delta-Hedged Short Strangle Payoff at Expiry"
    />
  </>
);

const StrategyDetailsModal = ({ isOpen, toggle, selectedResult, strategyType }) => {
  const structure = selectedResult?.derived;
  const isDhss = strategyType === "DELTA_HEDGED_SHORT_STRANGLE";

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>Strategy Details</ModalHeader>
      <ModalBody>
        {!selectedResult && (
          <Alert color="danger">Build failed — no result available.</Alert>
        )}

        {selectedResult?.error && (
          <Alert color="danger">
            <strong>Build failed:</strong> {selectedResult.error}
            {selectedResult.suggestions?.length > 0 && (
              <ul className="mt-2 mb-0">
                {selectedResult.suggestions.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            )}
          </Alert>
        )}

        {selectedResult?.warning && (
          <Alert color="warning">
            <strong>Note:</strong> {selectedResult.warning}
          </Alert>
        )}

        {structure && isDhss && (
          <DhssStructureDetails selectedResult={selectedResult} structure={structure} />
        )}

        {structure && !isDhss && (
          <>
            <div style={{ border: "1px solid #e0e0e0", borderRadius: "8px", padding: "16px", marginBottom: "20px", background: "#fafafa" }}>
              <h5 style={{ marginBottom: "12px" }}>Strategy Structure</h5>

              <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>
                {structure.opening_strangle_mode ? "Opening Call (Short, Far-OTM)" : "Call (Short)"}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", rowGap: "10px", marginBottom: 16 }}>
                <div><strong>Call Symbol</strong><br/>{structure.call_symbol ?? "—"}</div>
                <div><strong>Strike</strong><br/>{structure.call_strike != null ? `$${structure.call_strike}` : "—"}</div>
                <div><strong>Call LTP</strong><br/>{structure.call_ltp != null ? `$${structure.call_ltp}` : "—"}</div>
                <div><strong>Call Delta</strong><br/>{structure.call_delta != null ? structure.call_delta.toFixed(3) : "—"}</div>
                {!structure.opening_strangle_mode && (
                  <div><strong>Initial Hedge %</strong><br/>{structure.hedge_percent != null ? structure.hedge_percent.toFixed(1) + "%" : "—"}</div>
                )}
                <div><strong>Call Strike Ratio</strong><br/>{structure.achieved_ratio ?? "—"}</div>
              </div>

              {structure.csp_enabled && (
                <>
                  <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginBottom: 6, fontWeight: 600, borderTop: "1px solid #eee", paddingTop: 12 }}>
                    {structure.opening_strangle_mode ? "Opening Put (Short, Far-OTM)" : "Put (CSP — Cash Secured)"}
                  </div>
                  {structure.csp_error ? (
                    <div style={{ color: "#dc3545", fontSize: 13 }}>{structure.csp_error}</div>
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", rowGap: "10px", marginBottom: 16 }}>
                      <div><strong>Put Symbol</strong><br/>{structure.csp_symbol ?? "—"}</div>
                      <div><strong>Put Strike</strong><br/>{structure.put_strike != null ? `$${structure.put_strike}` : "—"}</div>
                      <div><strong>Put LTP</strong><br/>{structure.put_ltp != null ? `$${structure.put_ltp}` : "—"}</div>
                      <div><strong>Put Delta</strong><br/>{structure.put_delta != null ? structure.put_delta.toFixed(3) : "—"}</div>
                      <div><strong>Distance from LTP</strong><br/>{structure.put_distance_pct != null ? structure.put_distance_pct.toFixed(1) + "%" : "—"}</div>
                    </div>
                  )}
                </>
              )}

              <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginBottom: 6, fontWeight: 600, borderTop: "1px solid #eee", paddingTop: 12 }}>Position</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", rowGap: "10px" }}>
                <div><strong>Reference Avg Price</strong><br/>{structure.reference_avg_price != null ? `$${structure.reference_avg_price}` : "—"}</div>
                <div><strong>Lot Size</strong><br/>{structure.lot_size ?? "—"}</div>
                <div><strong>Target Lots</strong><br/>{structure.target_lots ?? "—"}</div>
                <div><strong>Next Buy Price</strong><br/>{structure.next_buy_price != null ? `$${structure.next_buy_price}` : "—"}</div>
                <div><strong>Next Buy Qty</strong><br/>{structure.next_buy_quantity ?? "—"}</div>
              </div>

              {structure.tail_risk_pnl != null && (() => {
                const minCsm = selectedResult?.intent?.settings_snapshot?.min_profit_csm;
                const clears = minCsm != null ? structure.tail_risk_pnl >= minCsm : null;
                return (
                  <div style={{
                    marginTop: 14, padding: "10px 12px", borderRadius: 6,
                    border: `1px solid ${clears === false ? "#f5c6cb" : "#c3e6cb"}`,
                    background: clears === false ? "#f8d7da" : "#d4edda",
                  }}>
                    <div style={{ fontSize: 11, color: "#555", textTransform: "uppercase", fontWeight: 600 }}>
                      Tail P&L at Completion (marked to call strike)
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: clears === false ? "#721c24" : "#155724" }}>
                      {fmtPnl(structure.tail_risk_pnl)}
                      {minCsm != null && (
                        <span style={{ fontSize: 12, fontWeight: 500, marginLeft: 8 }}>
                          {clears ? "✓ clears" : "✗ below"} Min Profit (CSM) target of {fmtPnl(minCsm)}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: "#777", marginTop: 2 }}>
                      This is the number the ladder is built to guarantee — the per-rung P&L columns below are illustrative mark-to-market at each rung's own price, not this figure.
                    </div>
                  </div>
                );
              })()}
            </div>

            <PayoffChart
              strategyType="PYRAMID_HEDGING"
              derived={structure}
              title="Pyramid Hedging Payoff at Expiry"
            />

            {structure.ladder?.length > 0 ? (
              <LadderTable structure={structure} />
            ) : structure.opening_strangle_mode ? (
              <Alert color="primary">
                <strong>Opening Strangle Mode</strong><br/>
                No stock position — strategy opens with <strong>{structure.target_lots} lot(s)</strong> of far-OTM call{structure.csp_enabled && !structure.csp_error ? " and put" : ""}. Stock accumulation will begin after the first buy trigger fires.
              </Alert>
            ) : structure.covered_call_mode ? (
              <Alert color="success">
                <strong>Traditional Covered Call</strong><br/>
                Position is fully invested at {structure.achievable_lots} lot(s) ({selectedResult?.runtime?.current_netqty} shares at avg ${structure.reference_avg_price}).
                Sell <strong>{structure.target_lots} call lot(s)</strong> of {structure.call_symbol} to set up a covered call. No additional stock buying required.
              </Alert>
            ) : (
              <Alert color="info">
                Current position ({selectedResult?.runtime?.current_netqty} shares at avg ${structure.reference_avg_price}) already covers the {structure.target_lots}-lot target ({structure.target_lots * structure.lot_size} shares). No additional buying required.
              </Alert>
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default PortfolioBuilder;
